/**
 * "Vista fyrir ónettengt" controls:
 *  - SaveOfflineButton({ songId }) — for the song top bar (placed by the viewer team / lead)
 *  - SongOfflineAction — the per-row action in song lists (icon shows the song's offline state)
 *  - PackButton — "Vista lagalista" / "Vista möppu" / "Vista allt mitt" with live status
 * All open SavePackSheet and render nothing where saving is not offered (iOS Safari tab).
 */
import { useQueryClient } from '@tanstack/react-query';
import { CloudAlert, CloudCheck, CloudDownload, CloudSync, LoaderCircle, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import type { SongDTO, SongListItemDTO } from '../../shared/dto/library';
import { SONGS_KEY, songKey } from '../api/library';
import { Button, cx } from '../components/ui';
import { useI18n, type MessageKey } from '../i18n';
import { BADGE_KEY, percent } from './format';
import { canSaveOffline, useOfflineSnapshot, usePack, useSongOfflineState } from './hooks';
import { SavePackSheet, STATUS_ICON } from './SavePackSheet';
import type { SongOfflineState } from './store';
import type { PackKind } from './types';

export const SONG_STATE_ICON: Record<SongOfflineState, LucideIcon> = {
  saved: CloudCheck,
  downloading: LoaderCircle,
  update: CloudSync,
  pending: CloudDownload,
  error: CloudAlert,
};

export const SONG_STATE_TONE: Record<SongOfflineState, string> = {
  saved: 'text-[var(--ok-text)]',
  downloading: 'text-[var(--accent)]',
  update: 'text-amber-600 dark:text-amber-300',
  pending: 'text-[var(--accent)]',
  error: 'text-[var(--danger-text)]',
};

/** Title of a song from the query cache (detail, else list). */
function useSongTitle(songId: string): () => string {
  const qc = useQueryClient();
  return () => qc.getQueryData<SongDTO>(songKey(songId))?.title ?? qc.getQueryData<SongListItemDTO[]>(SONGS_KEY)?.find((s) => s.id === songId)?.title ?? '';
}

/** Song top bar: icon (+ short label from sm up) showing the song's offline state; opens the sheet. */
export function SaveOfflineButton({ songId, className }: { songId: string; className?: string }) {
  const { t } = useI18n();
  const state = useSongOfflineState(songId);
  const titleOf = useSongTitle(songId);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  if (!canSaveOffline()) return null;
  const Icon = state ? SONG_STATE_ICON[state] : CloudDownload;
  const label = state ? t(BADGE_KEY[state]) : t('offline.save');
  const short: MessageKey = state === 'saved' ? 'offline.saved' : state === 'update' ? 'offline.status.update' : state === 'downloading' ? 'offline.status.downloading' : 'offline.save_short';
  return (
    <>
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => {
          setTitle(titleOf());
          setOpen(true);
        }}
        className={cx('inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-xl px-2 font-semibold transition hover:bg-[var(--accent-soft)]', className)}
      >
        <Icon className={cx('size-6', state === 'downloading' && 'animate-spin', state && SONG_STATE_TONE[state])} aria-hidden />
        <span className="hidden text-[15px] lg:inline">{t(short)}</span>
      </button>
      <SavePackSheet open={open} onClose={() => setOpen(false)} kind="song" refId={songId} title={title} />
    </>
  );
}

/** Row action in song lists: the icon is the song's offline state; tap → save / remove sheet. */
export function SongOfflineAction({ songId, title }: { songId: string; title: string }) {
  const { t } = useI18n();
  const state = useSongOfflineState(songId);
  const [open, setOpen] = useState(false);
  const Icon = state ? SONG_STATE_ICON[state] : CloudDownload;
  const label = `${state ? t(BADGE_KEY[state]) : t('offline.save')}: ${title}`;
  return (
    <>
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => setOpen(true)}
        className="mr-1 inline-flex size-11 shrink-0 items-center justify-center rounded-xl transition hover:bg-[var(--accent-soft)] md:mr-2"
      >
        <Icon className={cx('size-6', state === 'downloading' && 'animate-spin', state ? SONG_STATE_TONE[state] : 'text-[var(--muted)]')} aria-hidden />
      </button>
      <SavePackSheet open={open} onClose={() => setOpen(false)} kind="song" refId={songId} title={title} />
    </>
  );
}

/** "Vista lagalista" / "Vista möppu" / "Vista allt mitt" button with the pack's live status. */
export function PackButton({
  kind,
  refId,
  title,
  label,
  className,
  size = 'lg',
}: {
  kind: PackKind;
  refId: string | null;
  title: string;
  label: string;
  className?: string;
  size?: 'md' | 'lg';
}) {
  const { t } = useI18n();
  const pack = usePack(kind, refId);
  const snap = useOfflineSnapshot();
  const [open, setOpen] = useState(false);
  let text = label;
  let Icon: LucideIcon = CloudDownload;
  if (pack) {
    Icon = STATUS_ICON[pack.status];
    const bytesDone = pack.status === 'downloading' && snap.progress ? snap.progress.bytesDone : pack.bytesDone;
    text =
      pack.status === 'complete'
        ? t('offline.saved')
        : pack.status === 'downloading'
          ? `${t('offline.status.downloading')} ${t('offline.percent', { percent: percent(bytesDone, pack.bytesTotal) })}`
          : t(pack.status === 'update' ? 'offline.status.update' : pack.status === 'error' ? 'offline.status.error' : 'offline.status.pending');
  }
  return (
    <>
      <Button
        variant={pack ? 'secondary' : 'primary'}
        size={size}
        icon={Icon}
        onClick={() => setOpen(true)}
        className={cx(pack?.status === 'downloading' && '[&_svg]:animate-spin', pack?.status === 'complete' && 'text-[var(--ok-text)]', className)}
      >
        {text}
      </Button>
      <SavePackSheet open={open} onClose={() => setOpen(false)} kind={kind} refId={refId} title={title} />
    </>
  );
}
